/* PreNumbers Financial Builder — Dashboard (reskinned).
   REPLACES: src/components/FinancialBuilder/DashboardOverview/Dashboard.jsx

   getUpdatedCalculations + state + the /api/fb/getCalculationData wiring are
   unchanged. The old absolutely-positioned icon rail (DashboardSideBar /
   SidebarButton) is replaced by the collapsible FineTuneRail. */

import { useState, useContext, useEffect, useCallback } from 'react';

import { ProductFilled, DollarCircleFilled, BookFilled, SettingFilled, CalculatorFilled,
  RiseOutlined, PieChartOutlined, FundOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { RevenueVsCostChart, FinancialProjectionChart, RevenueGrowthChart, CostOverviewChart, BurnRateChart } from '../Charts/Charts.js';
import { CompanyDetails } from '../CompaniesPage/CompaniesPage.jsx';
import { genericPOSTDatabaseCall, ShowAndHide, RenderOneTable, RenderMultiSelectTable } from '../../helperFunctions.jsx';
import { FineTuneRail, FineTuneItem, PageHead, FbIcons } from '../fbUI.jsx';

import ProductsPage from '../ProductsPage/ProductsPage.jsx';
import FixedCostsPage from '../FixedCostsPage/FixedCostsPage.jsx';
import OtherVariablesCost from '../Misc/OtherVariblesCost.jsx';
import CostAllocations from '../Misc/CostAllocations.jsx';

const { ArrowLeftOutlined } = FbIcons;

const KPI_ICONS = [<RiseOutlined />, <PieChartOutlined />, <FundOutlined />, <ThunderboltOutlined />];

function KpiCards({ stat }) {
  if (!stat || !stat[0]) return null;
  return (
    <div className="pn-kpis">
      {stat[0].map((k, i) => (
        <div key={k} className={"pn-kpi" + (i === 0 ? "" : "")}>
          <div className="kh"><span className="ic">{KPI_ICONS[i % KPI_ICONS.length]}</span>{k}</div>
          <div className="kv">{stat[1][i]}</div>
          <div className="kd up">firm-wide</div>
        </div>
      ))}
    </div>
  );
}

function DashboardSideBar({ onClickUpdateCalc }) {
  return (
    <FineTuneRail>
      <FineTuneItem name="Products" icon={<ProductFilled />} itemInModal={<ProductsPage showNavigationControls={false} />} />
      <FineTuneItem name="Cost Allocations" icon={<DollarCircleFilled />} itemInModal={<CostAllocations />} />
      <FineTuneItem name="Fixed Costs" icon={<BookFilled />} itemInModal={<FixedCostsPage showNavigationControls={false} />} />
      <FineTuneItem name="Other Variables" icon={<SettingFilled />} itemInModal={<OtherVariablesCost />} />
      <div className="pn-rail-sep" />
      <FineTuneItem name="Calculate" isModal={false} icon={<CalculatorFilled />} onClickUpdateCalc={onClickUpdateCalc} />
    </FineTuneRail>
  );
}

function ChartCard({ title, sub, children, full }) {
  return (
    <div className={"pn-chart-card" + (full ? " full" : "")}>
      <div className="pn-chart-head"><div><div className="pn-chart-title">{title}</div>{sub && <div className="pn-chart-sub">{sub}</div>}</div></div>
      <div className="pn-chart-body">{children}</div>
    </div>
  );
}

function Dashboard() {
  const { goTo, companySelected, messageApi } = useContext(FinancialBuilderContext);
  const [formattedData, setFormattedData] = useState([["A", "A"], ["A", "A"]]);
  const [percentageData, setPercentageData] = useState([["A", "A"], ["A", "A"]]);
  const [dataLoading, setDataLoading] = useState(true);
  const [firm_wide_data, setFirm_wide_data] = useState({});
  const [kpiCardsStat, setKpiCardsStat] = useState([]);

  const getUpdatedCalculations = useCallback(async () => {
    messageApi.loading('Fetching updated calculations...', 0);
    try {
      const response = await genericPOSTDatabaseCall('/api/fb/getCalculationData', { company: companySelected }, "calculation data");
      setFormattedData(response.finalDATA.formattedData);
      setPercentageData(response.finalDATA.percentage_data);
      setFirm_wide_data({
        firmWideStat: response.finalDATA.firm_wide_stat,
        firmWideStatPercentage: response.finalDATA.firm_wide_stat_percentage,
        otherFirmWideStat: response.finalDATA.other_firm_wide_stat,
        arrRatiosFirmWide: response.finalDATA.arr_ratios_firm_wide,
      });
      // Set kpi cards
      var kpi_cards_info = JSON.parse(JSON.stringify(response.finalDATA.firm_wide_stat_percentage));
      kpi_cards_info[0].splice(3, 2, 'Runway (Months)', 'Firm Cost Efficiency %' ,'Market Cap at IPO', 'Investment Amount');
      kpi_cards_info[1].splice(3, 2, response.finalDATA.firm_wide_stat[1][5], response.finalDATA.other_firm_wide_stat[1][3]);
      
      setKpiCardsStat(kpi_cards_info);
      messageApi.destroy();
      messageApi.success('Calculations updated successfully');
    } catch (error) {
      console.error('Error fetching calculation data:', error);
      messageApi.destroy();
      messageApi.error('Failed to fetch updated calculations');
    }
    setDataLoading(false);
  }, [companySelected, messageApi]);

  useEffect(() => { getUpdatedCalculations(); /* eslint-disable-next-line */ }, []);

  const finalDATA = { formattedData, percentageData };

  return (
    <div className="pn-wrap fb-dashboard-overview">
      <PageHead eyebrow={companySelected?.name} title="Dashboard"
        sub={<>Firm-wide projections &amp; growth. Use <b>Fine-tune</b> to adjust inputs, then recalculate.</>}>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(2)}><ArrowLeftOutlined /> Back to Products</button>
      </PageHead>

      {!dataLoading && <KpiCards stat={kpiCardsStat} />}

      <ShowAndHide title={"Company Info"}>
        <div className="pn-card" style={{ cursor: 'default', padding: 20 }}>
          <CompanyDetails company={companySelected} editable={false} />
        </div>
      </ShowAndHide>

      {!dataLoading && (
        <>
          <ShowAndHide defaultOpen title="Firm-wide Financials">
            <div className="pn-fw-tables">
              <RenderOneTable name={"Firm Wide Stat %"} data={firm_wide_data.firmWideStatPercentage} />
              <RenderOneTable name={"Employees & Price"} data={firm_wide_data.firmWideStat} />
              <RenderOneTable name={"Other Firm Wide Stat"} data={firm_wide_data.otherFirmWideStat} />
              <RenderOneTable name={"ARR Ratios Firm Wide"} data={firm_wide_data.arrRatiosFirmWide} />
            </div>
          </ShowAndHide>

          <ShowAndHide defaultOpen title="Projections & Growth">
            <RenderMultiSelectTable data={formattedData} />
            <RenderMultiSelectTable data={percentageData} />
          </ShowAndHide>

          <div className="pn-charts">
            <ChartCard full title="Financial Projection" sub="Revenue · Cost · Net income">
              <FinancialProjectionChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Revenue vs Cost of Revenue %" sub="Revenue bars, cost % line">
              <RevenueVsCostChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Revenue Growth by Product %" sub="Per-product growth">
              <RevenueGrowthChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Fixed vs Variable Cost" sub="Cost structure over time">
              <CostOverviewChart finalDATA={finalDATA} />
            </ChartCard>
            <ChartCard title="Monthly Burn Rate" sub="Burn $ and usage %">
              <BurnRateChart finalDATA={finalDATA} />
            </ChartCard>
          </div>
        </>
      )}

      <DashboardSideBar onClickUpdateCalc={getUpdatedCalculations} />
    </div>
  );
}

export default Dashboard;
